import { AbstractControl, ReactiveFormsModule, ValidationErrors, Validator } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { CdkOverlayOrigin, ConnectedOverlayPositionChange, OverlayModule } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { LanguageService } from '../../../service/language.service';
import { ModelBase } from '../../../model/model-base';
import { InputControlBase } from '../../forms/input-control.base';
import { AstralFieldActionComponent } from '../astral-field-action/astral-field-action.component';
import {
  ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN,
  getAstralDropdownOverlayPositions,
  getAstralDropdownOverlayWidth,
} from '../astral-dropdown-overlay.util';
import { AstralFieldShellComponent } from '../astral-field-shell/astral-field-shell.component';
import { resolveAstralIconClass } from '../astral-icon-class.util';
import { AstralFieldAccessibilityDirective } from '../astral-field-accessibility.util';

@Component({
  selector: 'astral-typeahead',
  standalone: true,
  imports: [NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, OverlayModule, AstralFieldActionComponent, AstralFieldShellComponent, AstralFieldAccessibilityDirective],
  templateUrl: './astral-typeahead.component.html',
  styleUrls: ['./astral-typeahead.component.scss'],
})
export class AstralTypeaheadComponent extends InputControlBase implements AfterViewInit, DoCheck, OnChanges, Validator {
  private static nextId = 0;
  private readonly uid = `astral-typeahead-${++AstralTypeaheadComponent.nextId}`;
  private readonly selectionValidator = this.validate.bind(this);
  readonly overlayViewportMargin = ASTRAL_DROPDOWN_OVERLAY_VIEWPORT_MARGIN;
  private resizeObserver?: ResizeObserver;
  private interactionDisabled: boolean | undefined;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly languageService: LanguageService
  ) {
    super();
  }

  @Input() clearOthers = false;
  @Input() filterActions = false;
  @Input() doNotDisplayClear = false;
  @Input() focused = false;
  @Input() inputId: string;
  @Input() inputName: string;
  @Input() itemsSource: ModelBase[] = [];
  @Input() placeholderText = 'COMMON.TYPE_TO_SEARCH';
  @Input() span: number | 'full' = 1;
  @Input() supplementalLabel: string;
  @Input() withShell = true;

  @Output() readonly addonClick = new EventEmitter();

  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('panel') panel: ElementRef<HTMLDivElement>;
  @ViewChild('controlShell') controlShell: ElementRef<HTMLDivElement>;
  @ViewChild('overlayOrigin') overlayOrigin: CdkOverlayOrigin;
  @ViewChildren('optionButton') optionButtons: QueryList<ElementRef<HTMLButtonElement>>;

  displayValue = '';
  filteredItems: ModelBase[] = [];
  isFocused = false;
  isPanelOpen = false;
  isPanelAbove = false;
  activeOptionIndex = -1;
  overlayWidth: number;

  @HostBinding('tabindex') tabindex = -1;

  @HostBinding('style.grid-column')
  get hostGridColumn(): string | null {
    if (this.span === 'full') {
      return '1 / -1';
    }

    if (!this.span || this.span <= 1) {
      return null;
    }

    return `span ${this.span}`;
  }

  get resolvedInputId(): string {
    return this.inputId ?? this.uid;
  }

  get resolvedInputName(): string {
    if (this.inputName) {
      return this.inputName;
    }

    const caption = this.control?.caption
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return caption || this.uid;
  }

  get resolvedPlaceholderText(): string {
    return this.placeholderText ?? 'COMMON.TYPE_TO_SEARCH';
  }

  get resolvedSupplementalLabel(): string | null {
    return this.supplementalLabel ?? this.control?.supplementalLabel ?? null;
  }

  get readonlyIconClass(): string {
    return resolveAstralIconClass('lock') ?? '';
  }

  get showClearOthers(): boolean {
    if (this.isInteractionDisabled || !this.clearOthers || !this.control?.parent) {
      return false;
    }

    return Object.keys(this.control.parent.controls).length > 1 && (this.filterActions || this.hasValue);
  }

  get showClearThis(): boolean {
    return !this.isInteractionDisabled && !this.doNotDisplayClear && (this.filterActions || this.hasValue);
  }

  get hasValue(): boolean {
    return !!this.displayValue || !!this.control?.value;
  }

  get overlayPositions() {
    return getAstralDropdownOverlayPositions();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    return typeof control.value === 'string'
      ? {
          value: {
            message: 'COMMON.INCORRECT_VALUE',
            params: {},
          },
        }
      : null;
  }

  @HostListener('focus')
  focusHost() {
    if (this.isInteractionDisabled) return;
    this.input?.nativeElement.focus();
  }

  get activeOptionId(): string | null {
    return this.activeOptionIndex >= 0 ? this.optionId(this.activeOptionIndex) : null;
  }

  ngDoCheck(): void {
    const interactionDisabled = this.isInteractionDisabled;
    if (this.interactionDisabled === interactionDisabled) return;

    this.interactionDisabled = interactionDisabled;
    if (interactionDisabled) {
      this.isFocused = false;
      this.closePanel();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscape(event: Event): void {
    if (!this.isPanelOpen) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.closePanel();
  }

  @HostListener('window:resize')
  handleWindowResize(): void {
    if (!this.isPanelOpen) {
      return;
    }

    requestAnimationFrame(() => this.syncOverlayWidth());
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.control.addValidators(this.selectionValidator);
    this.control.updateValueAndValidity({ emitEvent: false });
    this.syncFromControl(this.control?.value);
    this.subscription.add(
      this.control.valueChanges.subscribe(value => {
        this.syncFromControl(value);
      })
    );
  }

  ngAfterViewInit(): void {
    this.syncOverlayWidth();

    if (typeof ResizeObserver !== 'undefined' && this.controlShell?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => this.syncOverlayWidth());
      this.resizeObserver.observe(this.controlShell.nativeElement);
    }

    if (this.focused) {
      this.input?.nativeElement.focus();
      this.isFocused = true;
      this.cd.detectChanges();
    }
  }

  override ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.control?.removeValidators(this.selectionValidator);
    this.control?.updateValueAndValidity({ emitEvent: false });
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemsSource'] || changes['control']) {
      // API defaults frequently arrive after the field has initialized. Repaint the
      // selected caption even when the parent intentionally set the control silently.
      this.syncFromControl(this.control?.value);
    }
  }

  clearAllOtherInputs(): boolean {
    if (this.isInteractionDisabled) return false;
    const parentControls = this.control?.parent?.controls;
    if (!parentControls) {
      return false;
    }

    for (const controlName of Object.keys(parentControls)) {
      const currentControl = this.control.parent.get(controlName);
      if (currentControl !== this.control) {
        currentControl.setValue(undefined);
      }
    }

    this.control.parent.markAsDirty();
    this.addonClick.emit(this.control);
    return false;
  }

  clearInput(): boolean {
    if (this.isInteractionDisabled) return false;
    this.displayValue = '';
    this.filteredItems = this.itemsSource?.slice() ?? [];
    this.control.setValue(null);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.addonClick.emit(this.control);
    this.closePanel();
    return false;
  }

  getItemCaption(item: ModelBase, consumerType: 'list' | 'field' = 'list'): string {
    if (!(item instanceof ModelBase)) {
      return '';
    }

    return item.toAutocompleteValue(consumerType, this.languageService.currentCultureLang);
  }

  handleBlur(): void {
    queueMicrotask(() => {
      const activeElement = this.elementRef.nativeElement.ownerDocument?.activeElement as Node | null;
      if (activeElement && (activeElement === this.input?.nativeElement || this.panel?.nativeElement?.contains(activeElement))) {
        return;
      }

      if (!this.isPanelOpen) {
        this.finalizeInteraction();
      }
    });
  }

  handleFocus(): void {
    if (this.isInteractionDisabled) return;
    this.isFocused = true;
    this.onfocus();
    this.updateFilteredItems(this.displayValue);
    this.openPanel();
  }

  handleInput(event: Event): void {
    if (this.isInteractionDisabled) return;
    const value = (event.target as HTMLInputElement).value;
    this.displayValue = value;
    this.control.setValue(value as any);
    this.control.markAsDirty();
    this.updateFilteredItems(value);
    this.openPanel();
  }

  handleInputKeydown(event: KeyboardEvent): void {
    if (this.isInteractionDisabled) {
      return;
    }

    if (event.key === 'Escape' && this.isPanelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closePanel();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.openPanel();
      this.moveActiveOption(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (!this.isPanelOpen) {
      return;
    }

    if (event.key === 'Enter' && this.activeOptionIndex >= 0) {
      const activeItem = this.filteredItems[this.activeOptionIndex];
      if (!activeItem) {
        return;
      }

      event.preventDefault();
      this.selectItem(activeItem);
    }
  }

  optionId(index: number): string {
    return `${this.resolvedInputId}-option-${index}`;
  }

  handleOptionMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  selectItem(item: ModelBase): void {
    if (this.isInteractionDisabled) return;
    this.control.setValue(item);
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.displayValue = this.getItemCaption(item, 'field');
    this.filteredItems = [item];
    this.closePanel();
  }

  private closePanel(): void {
    this.isPanelOpen = false;
    this.isPanelAbove = false;
    this.activeOptionIndex = -1;
  }

  private filterItems(value: string): ModelBase[] {
    const filter = (value ?? '').toLowerCase().trim();
    return (this.itemsSource ?? []).filter(item => this.getItemCaption(item, 'list').toLowerCase().includes(filter));
  }

  private resolveTypedValue(): void {
    if (!(typeof this.control?.value === 'string')) {
      return;
    }

    const content = this.control.value.toLowerCase().trim();
    if (!content) {
      this.control.setValue(null);
      this.displayValue = '';
      return;
    }

    const exact = (this.itemsSource ?? []).find(item => this.getItemCaption(item, 'list').toLowerCase() === content);
    if (exact) {
      this.selectItem(exact);
      return;
    }

    const matches = this.filterItems(content);
    if (matches.length === 1) {
      this.selectItem(matches[0]);
    }
  }

  private syncFromControl(value: any): void {
    if (value instanceof ModelBase) {
      this.displayValue = this.getItemCaption(value, 'field');
      this.filteredItems = this.itemsSource?.filter(item => item.id === value.id) ?? [];
      return;
    }

    if (typeof value === 'string') {
      this.displayValue = value;
      this.updateFilteredItems(value);
      return;
    }

    this.displayValue = '';
    this.filteredItems = this.itemsSource?.slice() ?? [];
  }

  private updateFilteredItems(value: string): void {
    this.filteredItems = value ? this.filterItems(value) : (this.itemsSource?.slice() ?? []);
    this.activeOptionIndex = -1;
  }

  handleOverlayOutsideClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (target && this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.finalizeInteraction();
  }

  handleOverlayPositionChange(event: ConnectedOverlayPositionChange): void {
    this.isPanelAbove = event.connectionPair.overlayY === 'bottom';
  }

  private openPanel(): void {
    if (this.isInteractionDisabled) {
      return;
    }

    this.isPanelOpen = this.filteredItems.length > 0;
    if (this.isPanelOpen) {
      this.syncOverlayWidth();
    }
  }

  private get isInteractionDisabled(): boolean {
    return !!this.control?.disabled || !!this.control?.isReadonly;
  }

  private syncOverlayWidth(): void {
    const shellWidth = this.controlShell?.nativeElement.getBoundingClientRect().width;
    const viewportWidth = this.elementRef.nativeElement.ownerDocument?.documentElement?.clientWidth;

    if (!shellWidth) {
      return;
    }

    if (!viewportWidth) {
      this.overlayWidth = shellWidth;
      return;
    }

    this.overlayWidth = getAstralDropdownOverlayWidth(shellWidth, viewportWidth);
  }

  private finalizeInteraction(): void {
    this.isFocused = false;
    this.resolveTypedValue();
    this.control?.markAsTouched();
    this.control?.updateValueAndValidity({ emitEvent: false });
    this.closePanel();
    this.onblur();
  }

  private moveActiveOption(direction: 1 | -1): void {
    if (!this.filteredItems.length) {
      this.activeOptionIndex = -1;
      return;
    }

    if (this.activeOptionIndex < 0) {
      this.activeOptionIndex = direction === 1 ? 0 : this.filteredItems.length - 1;
    } else {
      this.activeOptionIndex = (this.activeOptionIndex + direction + this.filteredItems.length) % this.filteredItems.length;
    }

    requestAnimationFrame(() => this.optionButtons?.get(this.activeOptionIndex)?.nativeElement.scrollIntoView({ block: 'nearest' }));
  }
}
